import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  UsersIcon,
  PhotoIcon,
  TagIcon,
  CogIcon,
  SparklesIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi, MarketplaceListing, Order } from '../api/marketplace';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalConfig } from '../contexts/GlobalConfigContext';
import toast from 'react-hot-toast';
import AdvancedAnalytics from '../components/marketplace/AdvancedAnalytics';
import AutomatedMarketing from '../components/marketplace/AutomatedMarketing';
import OrdersManager from '../components/marketplace/OrdersManager';
import ListingsManager from '../components/marketplace/ListingsManager';
import WhatsAppGroupsManager from '../components/marketplace/WhatsAppGroupsManager';
import MessageTemplatesManager from '../components/marketplace/MessageTemplatesManager';
import MarketplaceSettings from '../components/marketplace/MarketplaceSettings';
import IntegrationSettings from '../components/marketplace/IntegrationSettings';
import { getFacilityId } from '../utils/facilityUtils';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import CountUpNumber from '../components/ui/CountUpNumber';

interface DashboardStats {
  totalListings: number;
  activeListings: number;
  totalOrders: number;
  pendingOrders: number;
  totalRevenue: number;
  totalViews: number;
  totalInquiries: number;
  platformStats: {
    whatsapp: number;
    facebook: number;
    instagram: number;
    telegram: number;
    birdMarketplace: number;
  };
}

export default function Marketplace() {
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentListings, setRecentListings] = useState<MarketplaceListing[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [showAutomatedMarketing, setShowAutomatedMarketing] = useState(false);

  // Use the utility function to get a proper facility ID
  const facilityId = getFacilityId(user?.facilityId);

  // Handle URL-based tab navigation
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/marketplace/listings') {
      setActiveTab('listings');
    } else if (path === '/marketplace/orders') {
      setActiveTab('orders');
    } else if (path === '/marketplace/whatsapp') {
      setActiveTab('whatsapp');
    } else if (path === '/marketplace/templates') {
      setActiveTab('templates');
    } else if (path === '/marketplace/analytics') {
      setActiveTab('analytics');
    } else if (path === '/marketplace/automation') {
      setActiveTab('automation');
    } else if (path === '/marketplace/integration') {
      setActiveTab('integration');
    } else if (path === '/marketplace/settings') {
      setActiveTab('settings');
    } else if (path === '/marketplace') {
      setActiveTab('overview');
    } else {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  useEffect(() => {
    loadDashboardData();
  }, [facilityId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load analytics
      const analytics = await marketplaceApi.getFacilityAnalytics(facilityId);
      
      // Load recent listings
      const listingsResponse = await marketplaceApi.getListings(facilityId, { 
        limit: 5, 
        page: 1 
      });
      
      // Load recent orders
      const recentOrdersData = await marketplaceApi.getRecentOrders(facilityId, 5);
      
      // Load order summary
      const orderSummary = await marketplaceApi.getOrderSummary(facilityId);
      
      // Load platform stats
      const platformStats = await marketplaceApi.getPlatformOrderStats(facilityId);

      setStats({
        totalListings: analytics.totalListings || 0,
        activeListings: analytics.activeListings || 0,
        totalOrders: orderSummary.totalOrders || 0,
        pendingOrders: orderSummary.pendingOrders || 0,
        totalRevenue: orderSummary.totalRevenue || 0,
        totalViews: analytics.totalViews || 0,
        totalInquiries: analytics.totalInquiries || 0,
        platformStats: {
          whatsapp: platformStats.whatsapp || 0,
          facebook: platformStats.facebook || 0,
          instagram: platformStats.instagram || 0,
          telegram: platformStats.telegram || 0,
          birdMarketplace: platformStats.birdMarketplace || 0,
        }
      });

      setRecentListings(listingsResponse.listings);
      setRecentOrders(recentOrdersData);
    } catch (error) {
      console.error('Error loading marketplace data:', error);
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-100 text-emerald-800';
      case 'draft':
        return 'bg-violet-100 text-violet-800';
      case 'staging':
        return 'bg-amber-100 text-amber-800';
      case 'expired':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800';
      case 'shipped':
        return 'bg-cyan-100 text-cyan-800';
      case 'confirmed':
        return 'bg-amber-100 text-amber-800';
      case 'pending':
        return 'bg-violet-100 text-violet-800';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config?.currency?.default || 'ZAR',
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl px-4 pt-2 sm:px-6 lg:px-8">
        <PageHero
          title="Marketplace"
          subtitle="Manage your bird listings, orders, and multi-platform sales"
          actions={
            <>
              <Link to="/marketplace/listings/new" className="btn btn-primary focus-pop">
                <PlusIcon className="h-4 w-4" />
                New Listing
              </Link>
              <Link to="/marketplace/orders/new" className="btn btn-secondary focus-pop">
                <ShoppingCartIcon className="h-4 w-4" />
                New Order
              </Link>
            </>
          }
        />
      </div>

      {/* Navigation Tabs */}
      <div className="mt-4 border-b border-slate-200/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap gap-2 py-2">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'listings', name: 'Listings', icon: DocumentTextIcon },
              { id: 'orders', name: 'Orders', icon: ShoppingCartIcon },
              { id: 'whatsapp', name: 'WhatsApp', icon: ChatBubbleLeftRightIcon },
              { id: 'templates', name: 'Templates', icon: TagIcon },
              { id: 'analytics', name: 'Analytics', icon: ArrowTrendingUpIcon },
              { id: 'automation', name: 'Automation', icon: SparklesIcon },
              { id: 'integration', name: 'Integration', icon: GlobeAltIcon },
              { id: 'settings', name: 'Settings', icon: CogIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'overview') {
                    navigate('/marketplace');
                  } else {
                    navigate(`/marketplace/${tab.id}`);
                  }
                }}
                className={`tab-pill ${activeTab === tab.id ? 'tab-pill-active' : ''}`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {activeTab === 'overview' && (
          <div className="fade-up space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="interactive-card glass-panel fade-up overflow-hidden rounded-2xl" style={{ animationDelay: '0ms' }}>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Total Listings
                        </dt>
                        <dd className="text-lg font-medium text-slate-900">
                          <CountUpNumber value={stats?.totalListings || 0} />
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="interactive-card glass-panel fade-up overflow-hidden rounded-2xl" style={{ animationDelay: '45ms' }}>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ShoppingCartIcon className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Total Orders
                        </dt>
                        <dd className="text-lg font-medium text-slate-900">
                          <CountUpNumber value={stats?.totalOrders || 0} />
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="interactive-card glass-panel fade-up overflow-hidden rounded-2xl" style={{ animationDelay: '90ms' }}>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CurrencyDollarIcon className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Total Revenue
                        </dt>
                        <dd className="text-lg font-medium text-slate-900">
                          {formatCurrency(stats?.totalRevenue || 0)}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="interactive-card glass-panel fade-up overflow-hidden rounded-2xl" style={{ animationDelay: '135ms' }}>
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <EyeIcon className="h-6 w-6 text-cyan-500" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Total Views
                        </dt>
                        <dd className="text-lg font-medium text-slate-900">
                          <CountUpNumber value={stats?.totalViews || 0} />
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Performance */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                  Platform Performance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(stats?.platformStats || {}).map(([platform, count]) => (
                    <div key={platform} className="text-center fade-up" style={{ animationDelay: '120ms' }}>
                      <div className="text-2xl font-bold text-cyan-600">
                        <CountUpNumber value={count} />
                      </div>
                      <div className="text-sm text-slate-500 capitalize">{platform}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Listings */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-slate-900">
                      Recent Listings
                    </h3>
                    <Link
                      to="/marketplace/listings"
                      className="text-sm text-cyan-600 hover:text-cyan-500"
                    >
                      View all
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {recentListings.length > 0 ? recentListings.map((listing) => (
                      <div key={listing.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <img
                              className="h-10 w-10 rounded-lg object-cover"
                              src={listing.media.primaryImage || '/placeholder-bird.jpg'}
                              alt={listing.title}
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{listing.title}</p>
                            <p className="text-sm text-slate-500">{listing.specifications.species}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(listing.status)}`}>
                            {listing.status}
                          </span>
                          <span className="text-sm font-medium text-slate-900">
                            {formatCurrency(listing.price.amount)}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <EmptyState
                        title="No listings yet"
                        description="Create your first marketplace listing to start reaching buyers."
                        icon={<DocumentTextIcon className="h-6 w-6" />}
                        action={
                          <Link to="/marketplace/listings/new" className="btn btn-primary focus-pop">
                            <PlusIcon className="h-4 w-4" />
                            New Listing
                          </Link>
                        }
                        className="mt-2"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-slate-900">
                      Recent Orders
                    </h3>
                    <Link
                      to="/marketplace/orders"
                      className="text-sm text-cyan-600 hover:text-cyan-500"
                    >
                      View all
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {recentOrders.length > 0 ? recentOrders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            Order #{order.orderNumber}
                          </p>
                          <p className="text-sm text-slate-500">{order.buyer.name}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderStatusColor(order.orderStatus)}`}>
                            {order.orderStatus}
                          </span>
                          <span className="text-sm font-medium text-slate-900">
                            {formatCurrency(order.totalAmount)}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <EmptyState
                        title="No recent orders"
                        description="New orders will appear here as customers begin purchasing listings."
                        icon={<ShoppingCartIcon className="h-6 w-6" />}
                        className="mt-2"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Link
                    to="/marketplace/listings/new"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-slate-50/80 transition-colors"
                  >
                    <PlusIcon className="h-8 w-8 text-cyan-600 mb-2" />
                    <span className="text-sm font-medium text-slate-900">New Listing</span>
                  </Link>
                  
                  <Link
                    to="/marketplace/whatsapp"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-slate-50/80 transition-colors"
                  >
                    <ChatBubbleLeftRightIcon className="h-8 w-8 text-green-600 mb-2" />
                    <span className="text-sm font-medium text-slate-900">WhatsApp Groups</span>
                  </Link>
                  
                  <Link
                    to="/marketplace/templates"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-slate-50/80 transition-colors"
                  >
                    <TagIcon className="h-8 w-8 text-purple-600 mb-2" />
                    <span className="text-sm font-medium text-slate-900">Message Templates</span>
                  </Link>
                  
                  <Link
                    to="/marketplace/analytics"
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-slate-50/80 transition-colors"
                  >
                    <ArrowTrendingUpIcon className="h-8 w-8 text-orange-600 mb-2" />
                    <span className="text-sm font-medium text-slate-900">Analytics</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'listings' && (
          <ListingsManager />
        )}

        {activeTab === 'orders' && (
          <OrdersManager />
        )}

        {activeTab === 'whatsapp' && (
          <WhatsAppGroupsManager />
        )}

        {activeTab === 'templates' && (
          <MessageTemplatesManager />
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-medium text-slate-900">Analytics Dashboard</h3>
                <p className="text-sm text-slate-500">
                  Comprehensive performance insights across all platforms
                </p>
              </div>
              <button
                onClick={() => setShowAdvancedAnalytics(!showAdvancedAnalytics)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700"
              >
                <RocketLaunchIcon className="h-4 w-4 mr-2" />
                {showAdvancedAnalytics ? 'Basic Analytics' : 'Advanced Analytics'}
              </button>
            </div>
            
            {showAdvancedAnalytics ? (
              <AdvancedAnalytics />
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Basic Analytics & Reports
                  </h3>
                  <p className="text-slate-500">Click "Advanced Analytics" for detailed performance insights.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'automation' && (
          <AutomatedMarketing />
        )}

        {activeTab === 'integration' && (
          <IntegrationSettings />
        )}

        {activeTab === 'settings' && (
          <MarketplaceSettings />
        )}
      </div>
    </div>
  );
}
